import {useEffect, useMemo, useRef, useState} from "react";

import {useAppDispatch} from "../../../store/hooks";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY} from "../constants/prices_search";
import {ALL_ROOMS} from "../constants/rcn_rooms_filter";
import type {IPricesSearchNumberPrice, PricesSearchNumberPrices, RcnPricePeriod} from "../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../types/IRcnAddress";
import type {RcnPriceStatsQuery} from "../types/RcnPriceStatsQuery";
import {getRcnAddressKey} from "../utils/prices_search/group_rcn_addresses";
import {fetchRcnLatestPoint} from "../utils/resolve_rcn_latest_point";

/** Key of `PricesSearchNumberPrices`: the raw RCN values, the same format as the recent address id. */
export const getPricesSearchNumberPriceKey = getRcnAddressKey;

// A number that left the list skips its remaining grains; the number is fetched again if it comes back.
const ABANDONED = new Error("Prices search number price abandoned");

interface ISettledPrice {
    value: number;
    period: RcnPricePeriod;
}

// The price and its period, "unavailable" when RCN publishes none, or null when the requests failed.
type SettledNumberPrice = ISettledPrice | "unavailable" | null;

/**
 * Lazy transaction price per m² of building numbers and address rows, with the RCN period it comes from. Pass only
 * the addresses that are rendered, and an empty list when the street collapses. Each number resolves the address
 * page's all-rooms headline with the page's own queries (fetchRcnLatestPoint: the newest year of both markets first -
 * none means unavailable - then the newest month and quarter of both markets in parallel, the latest period winning),
 * so the dropdown shows the very figure and period the page opens with. A number is unavailable only when RCN answers
 * without a year point in either market - the address page redirects it to `?adres_niedostepny=1`. A failed request
 * shows no price and keeps the row clickable (the registry could not be reached, the address may well have data) -
 * and so does a cell that failed after the years answered, rather than an older grain's or one market's price the
 * page may not open with. At most PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY rendered numbers are resolved at once - a
 * number's 2-6 requests hold one slot - in the order given; numbers that stop being rendered leave the queue before
 * they are requested.
 *
 * A request already running is not aborted: getRcnData does not hand the abort signal to the fetch, so an
 * abort would only drop the answer (and the rtkq middleware reports RTK's AbortError to Bugsnag). A request for a
 * number that left the list still completes, but on the side: it no longer counts against the limit, and its
 * remaining grains are skipped. A settled price is kept for the life of the hook - so collapsing and
 * expanding again costs nothing.
 */
export const usePricesSearchNumberPrices = (numbers: readonly IRcnAddressKey[]): PricesSearchNumberPrices => {
    const dispatch = useAppDispatch();
    const [settled, setSettled] = useState<Readonly<Record<string, SettledNumberPrice>>>({});
    // The keys of `settled`, kept in step with it synchronously: the effect below can run with a `settled` closure from
    // before a number's settle landed while that number already left the in-flight map, and must not queue it again.
    const settledKeysRef = useRef(new Set<string>());
    // Running numbers by key, with the function that releases their cache subscriptions.
    const inFlightRef = useRef(new Map<string, () => void>());
    // Keys of the numbers rendered now: only they hold a slot, and a number's requests stop once it leaves.
    const wantedRef = useRef(new Set<string>());

    // A string dependency, so a new array with the same numbers restarts nothing.
    const numbersJson = JSON.stringify(numbers.map(({city, street, number}) => ({city, street, number})));

    // Runs again after every settled request, which is what hands a freed slot to the next number in line.
    useEffect(() => {
        const inFlight = inFlightRef.current;
        const wanted = JSON.parse(numbersJson) as IRcnAddressKey[];
        wantedRef.current = new Set(wanted.map(getPricesSearchNumberPriceKey));
        const queue = wanted.filter((address) => {
            const key = getPricesSearchNumberPriceKey(address);

            return !settledKeysRef.current.has(key) && !inFlight.has(key);
        });
        // A number left over from an earlier phrase finishes its current requests on the side; only rendered numbers hold a slot.
        const countRunning = () => [...inFlight.keys()].filter((key) => wantedRef.current.has(key)).length;

        while (countRunning() < PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY && queue.length > 0) {
            const address = queue.shift() as IRcnAddressKey;
            const key = getPricesSearchNumberPriceKey(address);
            const subscriptions: {unsubscribe: () => void}[] = [];
            let isReleased = false;
            let isAbandoned = false;
            const unsubscribe = () => {
                isReleased = true;
                subscriptions.forEach((subscription) => subscription.unsubscribe());
            };
            // After an unmount the next grains must not be asked for - they would subscribe with nobody to release them.
            const fetchRcn = (query: RcnPriceStatsQuery) => {
                if (isReleased) {
                    return Promise.reject(new Error("Prices search number price released"));
                }
                // The number left the list: skip the remaining grains.
                if (!wantedRef.current.has(key)) {
                    isAbandoned = true;

                    return Promise.reject(ABANDONED);
                }

                const subscription = dispatch(getRcnDataApi.endpoints.getRcnData.initiate(query));
                subscriptions.push(subscription);

                return subscription.unwrap();
            };

            inFlight.set(key, unsubscribe);

            // Dropped without settling, so the number is asked for again if it is rendered again.
            const drop = () => {
                if (inFlight.get(key) === unsubscribe) {
                    inFlight.delete(key);
                    unsubscribe();
                }
            };
            const settle = (value: SettledNumberPrice) => {
                // Unmounted in the meantime.
                if (inFlight.get(key) !== unsubscribe) {
                    return;
                }

                settledKeysRef.current.add(key);
                inFlight.delete(key);
                // The price lives in local state from now on, the cache entries are no longer needed.
                unsubscribe();
                setSettled((prev) => ({...prev, [key]: value}));
            };

            void fetchRcnLatestPoint(fetchRcn, address, ALL_ROOMS).then(
                ({resolved, failedGrains}) => {
                    if (isAbandoned) {
                        drop();
                    } else if (failedGrains.length > 0) {
                        settle(null);
                    } else if (resolved) {
                        const {value, period_start, period_label} = resolved.point;

                        settle({value, period: {period_start, period_label}});
                    } else {
                        settle("unavailable");
                    }
                },
                (error) => (error === ABANDONED ? drop() : settle(null))
            );
        }
    }, [numbersJson, settled, dispatch]);

    useEffect(() => {
        const inFlight = inFlightRef.current;

        return () => {
            inFlight.forEach((unsubscribe) => unsubscribe());
            inFlight.clear();
        };
    }, []);

    return useMemo(() => {
        const wanted = JSON.parse(numbersJson) as IRcnAddressKey[];

        return Object.fromEntries(
            wanted.map((address): [string, IPricesSearchNumberPrice] => {
                const key = getPricesSearchNumberPriceKey(address);

                if (!(key in settled)) {
                    return [key, {isLoading: true}];
                }

                const value = settled[key];

                if (value === "unavailable") {
                    return [key, {isLoading: false, isUnavailable: true}];
                }

                return [key, value ? {priceM2: value.value, pricePeriod: value.period, isLoading: false} : {isLoading: false}];
            })
        );
    }, [numbersJson, settled]);
};
import {useEffect, useMemo, useRef, useState} from "react";

import {useAppDispatch} from "../../../store/hooks";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY} from "../constants/prices_search";
import {ALL_ROOMS} from "../constants/rcn_rooms_filter";
import type {IPricesSearchNumberPrice, PricesSearchNumberPrices, RcnPricePeriod} from "../types/IPricesSearchModel";
import type {IRcnAddressKey} from "../types/IRcnAddress";
import type {RcnPriceStatsQuery} from "../types/RcnPriceStatsQuery";
import {getRcnAddressKey} from "../utils/prices_search/group_rcn_addresses";
import {fetchRcnLatestPoint} from "../utils/resolve_rcn_latest_point";

/** Key of `PricesSearchNumberPrices`: the raw RCN values, the same format as the recent address id. */
export const getPricesSearchNumberPriceKey = getRcnAddressKey;

// A number that left the list skips its remaining grains; the number is fetched again if it comes back.
const ABANDONED = new Error("Prices search number price abandoned");

interface ISettledPrice {
    value: number;
    period: RcnPricePeriod;
}

// The price and its period, "unavailable" when RCN publishes none, or null when the requests failed.
type SettledNumberPrice = ISettledPrice | "unavailable" | null;

/**
 * Lazy transaction price per m² of building numbers and address rows, with the RCN period it comes from. Pass only
 * the addresses that are rendered, and an empty list when the street collapses. Each number resolves the address
 * page's all-rooms headline with the page's own queries (fetchRcnLatestPoint: the newest year of both markets first -
 * none means unavailable - then the newest month and quarter of both markets in parallel, the latest period winning),
 * so the dropdown shows the very figure and period the page opens with. A number is unavailable only when RCN answers
 * without a year point in either market - the address page redirects it to `?adres_niedostepny=1`. A failed request
 * shows no price and keeps the row clickable (the registry could not be reached, the address may well have data) -
 * and so does a cell that failed after the years answered, rather than an older grain's or one market's price the
 * page may not open with. At most PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY rendered numbers are resolved at once - a
 * number's 2-6 requests hold one slot - in the order given; numbers that stop being rendered leave the queue before
 * they are requested.
 *
 * A request already running is not aborted: getRcnData does not hand the abort signal to the fetch, so an
 * abort would only drop the answer (and the rtkq middleware reports RTK's AbortError to Bugsnag). A request for a
 * number that left the list still completes, but on the side: it no longer counts against the limit, and its
 * remaining grains are skipped. A settled price is kept for the life of the hook - so collapsing and
 * expanding again costs nothing.
 */
export const usePricesSearchNumberPrices = (numbers: readonly IRcnAddressKey[]): PricesSearchNumberPrices => {
    const dispatch = useAppDispatch();
    const [settled, setSettled] = useState<Readonly<Record<string, SettledNumberPrice>>>({});
    // The keys of `settled`, kept in step with it synchronously: the effect below can run with a `settled` closure from
    // before a number's settle landed while that number already left the in-flight map, and must not queue it again.
    const settledKeysRef = useRef(new Set<string>());
    // Running numbers by key, with the function that releases their cache subscriptions.
    const inFlightRef = useRef(new Map<string, () => void>());
    // Keys of the numbers rendered now: only they hold a slot, and a number's requests stop once it leaves.
    const wantedRef = useRef(new Set<string>());

    // A string dependency, so a new array with the same numbers restarts nothing.
    const numbersJson = JSON.stringify(numbers.map(({city, street, number}) => ({city, street, number})));

    // Runs again after every settled request, which is what hands a freed slot to the next number in line.
    useEffect(() => {
        const inFlight = inFlightRef.current;
        const wanted = JSON.parse(numbersJson) as IRcnAddressKey[];
        wantedRef.current = new Set(wanted.map(getPricesSearchNumberPriceKey));
        const queue = wanted.filter((address) => {
            const key = getPricesSearchNumberPriceKey(address);

            return !settledKeysRef.current.has(key) && !inFlight.has(key);
        });
        // A number left over from an earlier phrase finishes its current requests on the side; only rendered numbers hold a slot.
        const countRunning = () => [...inFlight.keys()].filter((key) => wantedRef.current.has(key)).length;

        while (countRunning() < PRICES_SEARCH_NUMBER_PRICES_CONCURRENCY && queue.length > 0) {
            const address = queue.shift() as IRcnAddressKey;
            const key = getPricesSearchNumberPriceKey(address);
            const subscriptions: {unsubscribe: () => void}[] = [];
            let isReleased = false;
            let isAbandoned = false;
            const unsubscribe = () => {
                isReleased = true;
                subscriptions.forEach((subscription) => subscription.unsubscribe());
            };
            // After an unmount the next grains must not be asked for - they would subscribe with nobody to release them.
            const fetchRcn = (query: RcnPriceStatsQuery) => {
                if (isReleased) {
                    return Promise.reject(new Error("Prices search number price released"));
                }
                // The number left the list: skip the remaining grains.
                if (!wantedRef.current.has(key)) {
                    isAbandoned = true;

                    return Promise.reject(ABANDONED);
                }

                const subscription = dispatch(getRcnDataApi.endpoints.getRcnData.initiate(query));
                subscriptions.push(subscription);

                return subscription.unwrap();
            };

            inFlight.set(key, unsubscribe);

            // Dropped without settling, so the number is asked for again if it is rendered again.
            const drop = () => {
                if (inFlight.get(key) === unsubscribe) {
                    inFlight.delete(key);
                    unsubscribe();
                }
            };
            const settle = (value: SettledNumberPrice) => {
                // Unmounted in the meantime.
                if (inFlight.get(key) !== unsubscribe) {
                    return;
                }

                settledKeysRef.current.add(key);
                inFlight.delete(key);
                // The price lives in local state from now on, the cache entries are no longer needed.
                unsubscribe();
                setSettled((prev) => ({...prev, [key]: value}));
            };

            void fetchRcnLatestPoint(fetchRcn, address, ALL_ROOMS).then(
                ({resolved, failedGrains}) => {
                    if (isAbandoned) {
                        drop();
                    } else if (failedGrains.length > 0) {
                        settle(null);
                    } else if (resolved) {
                        const {value, period_start, period_label} = resolved.point;

                        settle({value, period: {period_start, period_label}});
                    } else {
                        settle("unavailable");
                    }
                },
                (error) => (error === ABANDONED ? drop() : settle(null))
            );
        }
    }, [numbersJson, settled, dispatch]);

    useEffect(() => {
        const inFlight = inFlightRef.current;

        return () => {
            inFlight.forEach((unsubscribe) => unsubscribe());
            inFlight.clear();
        };
    }, []);

    return useMemo(() => {
        const wanted = JSON.parse(numbersJson) as IRcnAddressKey[];

        return Object.fromEntries(
            wanted.map((address): [string, IPricesSearchNumberPrice] => {
                const key = getPricesSearchNumberPriceKey(address);

                if (!(key in settled)) {
                    return [key, {isLoading: true}];
                }

                const value = settled[key];

                if (value === "unavailable") {
                    return [key, {isLoading: false, isUnavailable: true}];
                }

                return [key, value ? {priceM2: value.value, pricePeriod: value.period, isLoading: false} : {isLoading: false}];
            })
        );
    }, [numbersJson, settled]);
};
